import { Button, Icon, Text } from "@chakra-ui/react";
import { RiDeleteBin6Line } from "react-icons/ri";
import { AvelaToast } from "src/hooks/useAvelaToast";
import { ConfirmProps } from "src/hooks/useConfirmationDialog";
import * as GQL from "src/types/graphql";
import { FormTemplateDefaultCard } from "../components/FormTemplateDefaultCard";
import * as LegacyRules from "./legacyRules";

export type Props = {
  legacyRules: LegacyRules.FormikType;
  confirm: (confirmProps?: ConfirmProps) => Promise<boolean>;
  toast: AvelaToast;
  refetch: () => Promise<void>;
  deleteRule: (
    variables: GQL.DeleteLegacyFormTemplateRuleVariables
  ) => Promise<void>;
  formTemplate: GQL.EditFormTemplateFragment;
  formTemplates: GQL.GetLegacyFormTemplateRuleByFormTemplateId_form_template[];
  onEditClick: () => Promise<void>;
};
export const LegacyRuleCard: React.FC<Props> = ({
  legacyRules,
  confirm,
  toast,
  refetch,
  formTemplate,
  formTemplates,
  deleteRule,
  onEditClick,
}: Props) => {
  const handleDeleteRule = async () => {
    if (await confirm()) {
      try {
        const ruleId = legacyRules.id;
        if (!ruleId) {
          throw new Error("Rule ID not found");
        }

        await deleteRule({
          formTemplateId: formTemplate.id,
          formTemplateRuleIds: [ruleId],
        });

        toast({
          title: "Success",
          description: "Rule deleted",
          status: "success",
        });

        // Refetch data to update the view
        await refetch();
      } catch (error) {
        console.error(error);
        toast.error({
          title: "Failed to delete rule",
          description: "An error occurred while deleting the rule.",
        });
      }
    }
  };

  return (
    <FormTemplateDefaultCard
      tag={
        <Text
          fontSize="sm"
          fontWeight="bold"
          color="gray.700"
          bg="white"
          border="1px solid"
          borderColor="gray.300"
          borderRadius="md"
          px={2}
          py={1}
          display="inline-block"
          width="fit-content"
        >
          Connect form
        </Text>
      }
      title={(() => {
        // Get the rule
        const rule = legacyRules;
        if (!rule || !rule.action.nextFormTemplateId) {
          return "Connect form";
        }

        // Find the matching form template
        const nextFormTemplate = formTemplates.find(
          (template) => template.id === rule.action.nextFormTemplateId
        );

        return nextFormTemplate?.name
          ? `Connect form to ${nextFormTemplate.name}`
          : "Connect form";
      })()}
      description="Connect this form to another when it is accepted"
      actionSwitch={
        <Button
          leftIcon={<Icon as={RiDeleteBin6Line} />}
          variant="outline"
          colorScheme="gray"
          size="sm"
          onClick={handleDeleteRule}
        >
          Delete
        </Button>
      }
      onEditClick={onEditClick}
    />
  );
};
